// Admin — Edit user (Wave E).
//
// Mirrors `tricivo_system/operations/edit_user.php`: profile + password +
// role/status. Server's PUT /users/:uuid accepts a partial; we send only
// fields the admin actually changed.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Select } from '../../../components/form/Select';
import { Textarea } from '../../../components/form/Textarea';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useStaffDetail, useUpdateStaff } from './hooks/useUsers';
import type { StaffRole, UpdateStaffInput } from '../../../types/api';
import styles from './UserFormPage.module.css';

const ROLE_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

export function UserEditPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detail = useStaffDetail(id);
  const update = useUpdateStaff(id ?? '');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [status, setStatus] = useState<'active' | 'deactivated'>('active');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!detail.data) return;
    setName(detail.data.name);
    setEmail(detail.data.email);
    setPhone(detail.data.phone ?? '');
    setAddress(detail.data.address ?? '');
    setPostcode(detail.data.postcode ?? '');
    setRole(detail.data.role);
    setStatus(detail.data.status);
  }, [detail.data]);

  if (detail.isLoading || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit User" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const original = detail.data;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch: UpdateStaffInput = {};
    if (name !== original.name) patch.name = name.trim();
    if (email !== original.email) patch.email = email.trim();
    if (phone !== (original.phone ?? '')) patch.phone = phone || null;
    if (address !== (original.address ?? '')) patch.address = address || null;
    if (postcode !== (original.postcode ?? '')) patch.postcode = postcode || null;
    if (role !== original.role) patch.role = role;
    if (status !== original.status) patch.status = status;
    if (newPassword) patch.new_password = newPassword;
    if (Object.keys(patch).length === 0) {
      navigate(`/admin/users/${encodeURIComponent(original.uuid)}`);
      return;
    }
    update.mutate(patch, {
      onSuccess: () => {
        navigate(`/admin/users/${encodeURIComponent(original.uuid)}`);
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit ${original.name}`}
        subtitle="Update profile, role, or password"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate(`/admin/users/${encodeURIComponent(original.uuid)}`)}
          >
            Back
          </Button>
        }
      />

      <Card title="Profile Information">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Email Address"
              variant="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Phone Number"
              variant="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <Input
              label="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
            />
          </div>

          <Textarea
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
          />

          <div className={styles.row}>
            <Select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as StaffRole)}
              options={ROLE_OPTIONS}
            />
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'deactivated')}
              options={STATUS_OPTIONS}
            />
          </div>

          <Input
            label="New Password (optional)"
            variant="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helper="Leave blank to keep the existing password. Min 8 characters."
            autoComplete="new-password"
          />

          <div className={styles.footer}>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate(`/admin/users/${encodeURIComponent(original.uuid)}`)
              }
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" icon={Save} loading={update.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
